import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  CircularProgress,
  Alert,
  Link,
  Paper
} from '@mui/material';
import { resetPassword } from '../../services/authService';

interface ForgotPasswordFormProps {
  onSignIn: () => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onSignIn }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const isValidInput = email.includes('@');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValidInput) return;
    
    setIsLoading(true);
    setError(null);

    const result = await resetPassword(email);
    
    setIsLoading(false);
    
    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.error || 'Failed to send password reset email');
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 400, mx: 'auto', mt: 8 }}>
      <Typography variant="h4" component="h1" align="center" gutterBottom>
        Reset Password
      </Typography>
      
      <Typography variant="body1" align="center" sx={{ mb: 3 }}>
        Enter your email address and we'll send you a link to reset your password.
      </Typography>
      
      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Password reset email sent! Check your inbox.
          </Alert>
        )}
        
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          name="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={email !== '' && !email.includes('@')}
          helperText={email !== '' && !email.includes('@') ? 'Enter a valid email' : ''}
          disabled={isLoading || success}
        />
        
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2, py: 1.5 }}
          disabled={isLoading || !isValidInput || success}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
        </Button>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Link 
            component="button" 
            variant="body2" 
            onClick={onSignIn} 
            sx={{ cursor: 'pointer' }}
          >
            Back to Sign In
          </Link>
        </Box>
      </Box>
    </Paper>
  );
};

export default ForgotPasswordForm; 